import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Phone } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 }
};

const pricingPlans = [
  {
    name: 'Companionship Visit',
    duration: '1 hour',
    price: '£20',
    description: 'Perfect for regular friendly visits and companionship.',
    features: [
      'Home companionship visit',
      'Conversation and company',
      'Light refreshment time',
      'Emotional support'
    ],
    popular: false
  },
  {
    name: 'Extended Visit',
    duration: '2 hours',
    price: '£38',
    description: 'More time for deeper connection and activities.',
    features: [
      'Extended companionship time',
      'Activity-based engagement',
      'Hobbies and games',
      'Unhurried conversation'
    ],
    popular: true
  },
  {
    name: 'Half-Day Support',
    duration: '4 hours',
    price: '£75',
    description: 'Comprehensive support for longer engagements.',
    features: [
      'Morning or afternoon support',
      'Multiple activities included',
      'Meals and refreshments',
      'Flexible scheduling'
    ],
    popular: false
  },
  {
    name: 'Outings & Appointments',
    duration: 'Variable',
    price: '£25',
    description: 'Accompanied trips and appointment support.',
    features: [
      'Local outing accompaniment',
      'Appointment support',
      'Shopping assistance',
      'Community activities'
    ],
    popular: false
  },
];

const additionalInfo = [
  'Bespoke support plans available for ongoing requirements',
  'Free initial consultation to discuss your needs',
  'No hidden costs or surprise fees',
  'Flexible cancellation with reasonable notice',
  'Family members can be invoiced directly',
  'Regular visit discounts may apply',
];

export const PricingPage = () => {
  return (
    <div className="min-h-screen bg-[#F7F9FC]">
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-24 bg-white" data-testid="pricing-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center max-w-3xl mx-auto">
            <span className="text-sm font-semibold uppercase tracking-[0.2em] text-[#3A7BD5] mb-4 block">
              Pricing
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#1E4F8A] leading-tight mb-6">
              Transparent & Fair Pricing
            </h1>
            <p className="text-lg text-[#4A5568] leading-relaxed">
              Quality companionship at accessible rates. Our pricing is clear, fair, and designed 
              to make premium companionship services available to those who need them.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-20 md:py-28 bg-[#F7F9FC]" data-testid="pricing-cards">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {pricingPlans.map((plan, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative"
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                    <span className="bg-[#3A7BD5] text-white text-xs font-semibold px-4 py-1 rounded-full">
                      Most Popular
                    </span>
                  </div>
                )}
                <Card className={`h-full border-2 ${plan.popular ? 'border-[#3A7BD5] shadow-lg' : 'border-[#E2E8F0]'} bg-white hover:shadow-xl transition-shadow`}>
                  <CardHeader className="text-center pb-4">
                    <CardTitle className="font-serif text-xl text-[#1E4F8A]">{plan.name}</CardTitle>
                    <CardDescription className="text-[#4A5568]">{plan.duration}</CardDescription>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="text-center mb-6">
                      <span className="text-4xl font-bold text-[#1E4F8A]">From {plan.price}</span>
                    </div>
                    <p className="text-sm text-[#4A5568] text-center mb-6">{plan.description}</p>
                    <ul className="space-y-3 mb-6">
                      {plan.features.map((feature, fIndex) => (
                        <li key={fIndex} className="flex items-start gap-2 text-sm text-[#4A5568]">
                          <CheckCircle2 className="w-4 h-4 text-[#3A7BD5] flex-shrink-0 mt-0.5" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Link
                      to="/contact#consultation-form"
                      className={`block text-center py-3 px-6 rounded-full font-medium transition-colors ${
                        plan.popular
                          ? 'bg-[#1E4F8A] text-white hover:bg-[#163A66]'
                          : 'border-2 border-[#1E4F8A] text-[#1E4F8A] hover:bg-[#F7F9FC]'
                      }`}
                      data-testid={`pricing-cta-${index}`}
                    >
                      Get Started
                    </Link>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Tailored Packages */}
      <section className="py-20 md:py-28 bg-white" data-testid="tailored-packages">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp}>
            <Card className="bg-[#1E4F8A] border-0 overflow-hidden">
              <CardContent className="p-8 md:p-12 text-center">
                <h2 className="font-serif text-3xl md:text-4xl text-white mb-4">
                  Tailored Packages
                </h2>
                <p className="text-white/80 mb-8 max-w-xl mx-auto">
                  Need something specific? We create bespoke companionship arrangements designed 
                  around your exact requirements, schedule, and budget.
                </p>
                <Link
                  to="/contact"
                  className="inline-flex items-center bg-white text-[#1E4F8A] px-8 py-4 rounded-full font-medium hover:bg-[#F7F9FC] transition-colors"
                  data-testid="tailored-contact"
                >
                  Contact Us to Discuss
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Additional Information */}
      <section className="py-20 md:py-28 bg-[#F7F9FC]" data-testid="additional-info">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center mb-12">
            <h2 className="font-serif text-3xl md:text-4xl text-[#1E4F8A] mb-4">
              Good to Know
            </h2>
            <p className="text-[#4A5568]">
              Important information about our pricing and services.
            </p>
          </motion.div>
          <motion.div {...fadeInUp}>
            <Card className="bg-white border-[#E2E8F0]">
              <CardContent className="p-8">
                <ul className="grid md:grid-cols-2 gap-4">
                  {additionalInfo.map((info, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-[#3A7BD5] flex-shrink-0 mt-0.5" />
                      <span className="text-[#4A5568]">{info}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Pricing Note */}
      <section className="py-12 bg-white" data-testid="pricing-note">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center">
            <p className="text-[#4A5568] text-sm">
              <strong className="text-[#1A1A1A]">Please note:</strong> Pricing may vary depending on 
              location, visit duration, and individual service requirements. All prices shown are 
              starting points. A free consultation will help determine the exact pricing for your needs.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-[#F7F9FC]" data-testid="pricing-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeInUp}>
            <Phone className="w-12 h-12 text-[#3A7BD5] mx-auto mb-6" />
            <h2 className="font-serif text-3xl md:text-4xl text-[#1E4F8A] mb-6">
              Questions About Pricing?
            </h2>
            <p className="text-lg text-[#4A5568] mb-8">
              We're happy to discuss your specific needs and provide a clear quote. 
              Get in touch for a free, no-obligation consultation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
  to="/contact#consultation-form"
  className="inline-flex items-center justify-center bg-[#1E4F8A] text-white px-8 py-4 rounded-full font-medium hover:bg-[#163A66] transition-colors"
>
  Book Free Consultation
  <ArrowRight className="w-5 h-5 ml-2" />
</Link>

<a
  href="tel:07803174495"
  className="inline-flex items-center justify-center border-2 border-[#1E4F8A] text-[#1E4F8A] px-8 py-4 rounded-full font-medium hover:bg-white transition-colors"
>
  Call 07803 174495
</a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PricingPage;
